import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTA } from "@/components/CTA";
import { Badge } from "@/components/ui/badge";
import { getCategoryIcon } from "@/lib/categoryIcons";
import { Star as StarIcon } from "@phosphor-icons/react";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";
import { resolveAssetUrl } from "@/lib/media";

interface WorkItem {
  id: string;
  title: string;
  title_ar: string | null;
  slug: string;
  featured_image: string | null;
  desktop_screenshot: string | null;
  industry: string | null;
  short_description: string | null;
  short_description_ar: string | null;
  website_url: string | null;
  featured: boolean;
  show_in_header: boolean;
}

export default function WorkIndex() {
  const [work, setWork] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndustry, setActiveIndustry] = useState<string | null>(null);
  const { t, lang, localePath } = useLanguage();

  const { canonical, hreflang } = buildHreflang("/work");
  useCmsPageSEO({
    title: t("Our Work | Wavai Projects in Kuwait & GCC", "أعمالنا | مشاريع وافاي في الكويت ودول الخليج"),
    description: t("See the Shopify, WordPress, Webflow and AI projects Wavai has delivered for leading brands in Kuwait, the GCC and beyond.", "اطّلع على مشاريع شوبيفاي وووردبريس وويبفلو والذكاء الاصطناعي التي نفّذتها وافاي لأبرز العلامات في الكويت ودول الخليج وما وراءها."),
    canonical,
    hreflang,
  });

  useEffect(() => {
    const fetchWork = async () => {
      const { data } = await supabase
        .from("work")
        .select("id, title, title_ar, slug, featured_image, desktop_screenshot, industry, short_description, short_description_ar, website_url, featured, show_in_header")
        .eq("status", "published")
        .order("sort_order");
      setWork(data || []);
      setLoading(false);
    };
    fetchWork();
  }, [lang]);

  const industries = useMemo(() => {
    const set = new Set(work.map((w) => w.industry).filter(Boolean));
    return Array.from(set).sort();
  }, [work]);

  const filtered = activeIndustry
    ? work.filter((w) => w.industry === activeIndustry)
    : work;

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Header />
      <main className="overflow-x-hidden">
        <section className="pt-28 pb-8 sm:pb-12 lg:pt-40 lg:pb-20 relative overflow-x-hidden">
          <div className="absolute inset-0 bg-gradient-hero opacity-40" />
          <div className="container px-4 sm:px-6 relative z-10">
            <div className="max-w-2xl mx-auto text-center">
              <p className="text-xs sm:text-sm uppercase tracking-[0.18em] text-primary font-medium mb-3 sm:mb-4">
                {t("Portfolio", "معرض الأعمال")}
              </p>
              <h1 className="text-[2.1rem] sm:text-4xl lg:text-5xl xl:text-6xl font-display font-bold mb-3 sm:mb-4 lg:mb-6 leading-[1.02] tracking-tight text-balance">
                {t("Our Work in", "أعمالنا في")} <span className="text-gradient">{t("Kuwait & the GCC", "الكويت ودول الخليج")}</span>
              </h1>
              <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-lg mx-auto leading-relaxed">
                {t(
                  "A selection of Shopify, Webflow, WordPress and AI projects shipped by Wavai for Kuwait's most ambitious brands — from heritage retailers to government entities to fast-growing startups.",
                  "مجموعة مختارة من مشاريع شوبيفاي وويبفلو وووردبريس والذكاء الاصطناعي التي نفّذتها وافاي لأبرز العلامات الكويتية — من المتاجر العريقة إلى الجهات الحكومية والشركات الناشئة سريعة النمو."
                )}
              </p>
            </div>
          </div>
        </section>

        <section className="py-6 sm:py-8 border-y border-border/50 bg-background/95">
          <div className="container px-4 sm:px-6 overflow-hidden">
            <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1 pr-20 sm:pr-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:pb-0">
              <button
                onClick={() => setActiveIndustry(null)}
                className={`flex-shrink-0 px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                  !activeIndustry ? "bg-primary text-primary-foreground shadow-sm" : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {t("All", "الكل")}
              </button>
              {industries.map((ind) => {
                const IconComp = getCategoryIcon(ind as string);
                return (
                  <button
                    key={ind}
                    onClick={() => setActiveIndustry(ind as string)}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium transition-colors ${
                      activeIndustry === ind ? "bg-primary text-primary-foreground shadow-sm" : "bg-secondary text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <IconComp size={16} weight="duotone" />
                    {ind}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-8 sm:py-12 lg:py-32">
          <div className="container px-4 sm:px-6">
            {loading ? (
              <p className="text-center text-muted-foreground">{t("Loading projects...", "جاري تحميل المشاريع...")}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-2.5 lg:gap-3">
                {filtered.map((item) => {
                  const imageSrc = resolveAssetUrl(item.featured_image);

                  return (
                    <Link
                      key={item.id}
                      to={localePath(`/work/${item.slug}`)}
                      className="group block w-full max-w-full rounded-2xl bg-card border border-transparent hover:border-primary/30 transition-all duration-300 hover:shadow-glow overflow-hidden"
                    >
                      {item.featured_image && (
                        <div className="p-2 sm:p-3 pb-0">
                          <div className="aspect-[16/11] overflow-hidden bg-secondary/50 border border-border/60 rounded-none group-hover:rounded-xl transition-[border-radius] duration-150 ease-out">
                            <img
                              src={imageSrc}
                              alt={t(item.title, item.title_ar)}
                              className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                              loading="lazy"
                            />
                          </div>
                        </div>
                      )}
                      <div className="p-4 sm:p-5">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex flex-wrap items-center gap-2">
                            {item.industry && (() => {
                              const IconComp = getCategoryIcon(item.industry);
                              return (
                                <Badge variant="secondary" className="text-xs inline-flex items-center gap-1">
                                  <IconComp size={12} weight="duotone" />
                                  {item.industry}
                                </Badge>
                              );
                            })()}
                          </div>
                          {item.featured && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-amber-500/10 to-amber-400/10 border border-amber-400/30 text-[11px] font-semibold text-amber-600 tracking-wide uppercase">
                              <StarIcon size={12} weight="fill" className="text-amber-500" />
                              {t("Featured", "مميز")}
                            </span>
                          )}
                        </div>
                        <h3 className="font-sans font-medium text-base group-hover:text-primary transition-colors mb-1.5">
                          {t(item.title, item.title_ar)}
                        </h3>
                        {(item.short_description || item.short_description_ar) && (
                          <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                            {t(item.short_description || "", item.short_description_ar)}
                          </p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <p className="text-center text-muted-foreground py-12">{t("No projects found.", "لم يتم العثور على مشاريع.")}</p>
            )}
          </div>
        </section>

        <CTA />
      </main>
      <Footer />
    </div>
  );
}
